import { IOrderHistoryFilterResponse } from '../../models/purchase-history';

export const CPS_FILTERS_CACHE_TTL_MS = 10 * 60 * 1000;
export const CPS_FILTERS_CACHE_MAX_ENTRIES = 100;

interface IOrderHistoryFilterCacheEntry {
  data: IOrderHistoryFilterResponse;
  createdAt: number;
}

export class CpsOrderHistoryFilterCache extends Map<
  string,
  IOrderHistoryFilterCacheEntry
> {
  private readonly maxEntries: number;

  private readonly ttlMs: number;

  constructor(maxEntries: number, ttlMs: number) {
    super();
    this.maxEntries = maxEntries;
    this.ttlMs = ttlMs;
  }

  override get(key: string): IOrderHistoryFilterCacheEntry | undefined {
    const entry = super.get(key);
    if (!entry) {
      return undefined;
    }
    if (this.isExpired(entry)) {
      super.delete(key);
      return undefined;
    }
    return entry;
  }

  override set(key: string, value: IOrderHistoryFilterCacheEntry): this {
    const entryWithTimestamp: IOrderHistoryFilterCacheEntry = {
      ...value,
      createdAt: value.createdAt || Date.now(),
    };
    this.pruneExpired();
    const result = super.set(key, entryWithTimestamp);
    this.enforceMaxSize();
    return result;
  }

  private isExpired(entry: IOrderHistoryFilterCacheEntry): boolean {
    return Date.now() - entry.createdAt > this.ttlMs;
  }

  private pruneExpired(): void {
    this.forEach((entry, key) => {
      if (this.isExpired(entry)) {
        super.delete(key);
      }
    });
  }

  private enforceMaxSize(): void {
    if (this.size <= this.maxEntries) {
      return;
    }
    const entriesByAge: Array<[string, IOrderHistoryFilterCacheEntry]> =
      Array.from(this.entries());
    entriesByAge.sort(
      (entryLeft, entryRight) =>
        (entryLeft[1].createdAt || 0) - (entryRight[1].createdAt || 0),
    );
    while (this.size > this.maxEntries && entriesByAge.length > 0) {
      const [oldestKey] = entriesByAge.shift() as [
        string,
        IOrderHistoryFilterCacheEntry,
      ];
      super.delete(oldestKey);
    }
  }
}
